// frontend/src/services/documentoService.js - SERVICIO DE DOCUMENTOS MEJORADO

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

class DocumentoService {
  constructor() {
    this.baseURL = `${API_BASE_URL}/documentos`;
    this.axiosInstance = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Interceptor para agregar token de autenticación
    this.axiosInstance.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Interceptor para manejar respuestas
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error('Error en DocumentoService:', error);
        return Promise.reject(error);
      }
    );
  }

  // ========== MÉTODOS NUEVOS PARA ETAPAS ==========

  // Obtener documentos por etapa (NUEVO)
  async getDocumentosPorEtapa(historiaEspecialidadId, etapaId) {
    try {
      const response = await this.axiosInstance.get(`/etapa/${etapaId}/historia/${historiaEspecialidadId}`);
      return {
        data: response.data?.documentos || [],
        mensaje: response.data?.mensaje
      };
    } catch (error) {
      // Mock data para desarrollo
      if (error.response?.status === 404 || !navigator.onLine) {
        return {
          data: this.getMockDocumentos(historiaEspecialidadId, etapaId),
          mensaje: 'Documentos cargados desde cache local'
        };
      }
      throw error;
    }
  }

  // ========== MÉTODOS ORIGINALES MANTENIDOS ==========
  
  // Obtener documentos generales (método original)
  async getDocumentos(params = {}) {
    try {
      const response = await this.axiosInstance.get('/', { params });
      return response.data;
    } catch (error) {
      if (error.response?.status === 404 || !navigator.onLine) {
        return { data: this.getMockDocumentosGenerales(), total: 10 };
      }
      throw error;
    }
  }

  // Obtener documento por ID (método original)
  async getDocumento(id) {
    try {
      const response = await this.axiosInstance.get(`/${id}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 404 || !navigator.onLine) {
        return { data: this.getMockDocumentoById(id) };
      }
      throw error;
    }
  }

  // Obtener documentos requeridos por etapa
  async getDocumentosRequeridosPorEtapa(etapaId) {
    try {
      const response = await this.axiosInstance.get(`/requeridos/etapa/${etapaId}`);
      return {
        data: response.data?.documentos_requeridos || []
      };
    } catch (error) {
      // Mock data para desarrollo
      if (error.response?.status === 404 || !navigator.onLine) {
        return {
          data: this.getMockDocumentosRequeridos(etapaId)
        };
      }
      throw error;
    }
  }

  // Subir documento
  async subirDocumento(formData) {
    try {
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        }       
      };

      const response = await this.axiosInstance.post('/subir', formData, config);
      return {
        data: response.data?.documento,
        mensaje: response.data?.mensaje || 'Documento subido correctamente'
      };
    } catch (error) {
      // Simulación para desarrollo
      if (error.response?.status === 404 || !navigator.onLine) {
        // Simular delay de subida
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const file = formData.get('file');
        const mockDocumento = {
          id: Date.now(),
          nombre_original: file.name,
          nombre_archivo: `doc_${Date.now()}_${file.name}`,
          tipo_mime: file.type,
          tamaño: file.size,
          ruta_archivo: `/uploads/documentos/${Date.now()}_${file.name}`,
          fecha_subida: new Date().toISOString(),
          historia_especialidad_id: formData.get('historia_especialidad_id'),
          etapa_id: formData.get('etapa_id'),
          paciente_id: formData.get('paciente_id'),
          tipo_documento_id: formData.get('tipo_documento_id'),
          tipo_documento: formData.get('tipo_documento_id') ? 
            this.getMockTipoDocumento(formData.get('tipo_documento_id')) : null
        };
        
        return {
          data: mockDocumento,
          mensaje: 'Documento subido correctamente (simulación)'
        };
      }
      throw error;
    }
  }

  // Descargar documento
  async descargarDocumento(documentoId) {
    try {
      const response = await this.axiosInstance.get(`/${documentoId}/descargar`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      // Simulación para desarrollo
      if (error.response?.status === 404 || !navigator.onLine) {
        // Crear un blob mock para simular descarga
        const mockContent = 'Este es un archivo simulado para desarrollo.\n\nContenido del documento...';
        return new Blob([mockContent], { type: 'text/plain' });
      }
      throw error;
    }
  }

  // Eliminar documento
  async eliminarDocumento(documentoId) {
    try {
      const response = await this.axiosInstance.delete(`/${documentoId}`);
      return {
        data: response.data,
        mensaje: response.data?.mensaje || 'Documento eliminado correctamente'
      };
    } catch (error) {
      // Simulación para desarrollo
      if (error.response?.status === 404 || !navigator.onLine) {
        await new Promise(resolve => setTimeout(resolve, 500));
        return {
          data: { success: true },
          mensaje: 'Documento eliminado correctamente (simulación)'
        };
      }
      throw error;
    }
  }

  // Obtener tipos de documentos
  async getTiposDocumentos() {
    try {
      const response = await this.axiosInstance.get('/tipos');
      return {
        data: response.data?.tipos || []
      };
    } catch (error) {
      // Mock data para desarrollo
      if (error.response?.status === 404 || !navigator.onLine) {
        return {
          data: this.getMockTiposDocumentos()
        };
      }
      throw error;
    }
  }

  // Mock data methods para desarrollo (NUEVOS)
  getMockDocumentos(historiaEspecialidadId, etapaId) {
    return [
      {
        id: 1,
        nombre_original: 'consentimiento_informado.pdf',
        nombre_archivo: 'doc_1_consentimiento_informado.pdf',
        tipo_mime: 'application/pdf',
        tamaño: 245760,
        ruta_archivo: '/uploads/documentos/doc_1_consentimiento_informado.pdf',
        fecha_subida: new Date(Date.now() - 86400000).toISOString(), // Ayer
        historia_especialidad_id: historiaEspecialidadId,
        etapa_id: etapaId,
        paciente_id: 1,
        tipo_documento_id: 1,
        tipo_documento: {
          id: 1,
          nombre: 'Consentimiento Informado',
          descripcion: 'Documento de autorización del paciente'
        }
      },
      {
        id: 2,
        nombre_original: 'historia_clinica_previa.doc',
        nombre_archivo: 'doc_2_historia_clinica_previa.doc',
        tipo_mime: 'application/msword',
        tamaño: 156420,
        ruta_archivo: '/uploads/documentos/doc_2_historia_clinica_previa.doc',
        fecha_subida: new Date(Date.now() - 172800000).toISOString(), // Hace 2 días
        historia_especialidad_id: historiaEspecialidadId,
        etapa_id: etapaId,
        paciente_id: 1,
        tipo_documento_id: 2,
        tipo_documento: {
          id: 2,
          nombre: 'Historia Clínica Previa',
          descripcion: 'Documentos médicos anteriores'
        }
      }
    ];
  }

  getMockDocumentosGenerales() {
    return [
      { id: 1, nombre: 'Documento General 1', tipo: 'PDF', fecha: new Date().toISOString() },
      { id: 2, nombre: 'Documento General 2', tipo: 'DOC', fecha: new Date().toISOString() }
    ];
  }

  getMockDocumentoById(id) {
    return { id, nombre: `Documento ${id}`, tipo: 'PDF', fecha: new Date().toISOString() };
  }

  getMockDocumentosRequeridos(etapaId) {
    return [
      {
        id: 1,
        nombre: 'Consentimiento Informado',
        descripcion: 'Documento firmado por el paciente autorizando el tratamiento',
        requerido: true,
        etapa_id: etapaId
      },
      {
        id: 2,
        nombre: 'Historia Clínica Previa',
        descripcion: 'Documentos médicos y psicológicos anteriores del paciente',
        requerido: true,
        etapa_id: etapaId
      },
      {
        id: 3,
        nombre: 'Resultados de Evaluación',
        descripcion: 'Tests y evaluaciones psicológicas iniciales',
        requerido: false,
        etapa_id: etapaId
      },
      {
        id: 4,
        nombre: 'Autorización de Familiar',
        descripcion: 'En caso de menores de edad o tutela',
        requerido: false,
        etapa_id: etapaId
      }
    ];
  }

  getMockTiposDocumentos() {
    return [
      {
        id: 1,
        nombre: 'Consentimiento Informado',
        descripcion: 'Documento de autorización del paciente',
        activo: true
      },
      {
        id: 2,
        nombre: 'Historia Clínica Previa',
        descripcion: 'Documentos médicos anteriores',
        activo: true
      },
      {
        id: 3,
        nombre: 'Resultados de Evaluación',
        descripcion: 'Tests y evaluaciones psicológicas',
        activo: true
      },
      {
        id: 4,
        nombre: 'Autorización de Familiar',
        descripcion: 'Documentos de autorización familiar',
        activo: true
      },
      {
        id: 5,
        nombre: 'Informe Médico',
        descripción: 'Informes de otros profesionales',
        activo: true
      }
    ];
  }

  getMockTipoDocumento(tipoId) {
    const tipos = this.getMockTiposDocumentos();
    return tipos.find(tipo => tipo.id === parseInt(tipoId)) || null;
  }

  // Validar archivo
  validarArchivo(file) {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/jpeg',
      'image/png',
      'image/gif',
      'text/plain'
    ];

    if (file.size > maxSize) {
      throw new Error('El archivo es demasiado grande. Máximo 10MB permitido.');
    }

    if (!allowedTypes.includes(file.type)) {
      throw new Error('Tipo de archivo no permitido. Solo se permiten PDF, DOC, DOCX, JPG, PNG, GIF y TXT.');
    }

    return true;
  }

  // Obtener estadísticas de documentos
  async getEstadisticasDocumentos(historiaEspecialidadId) {
    try {
      const response = await this.axiosInstance.get(`/estadisticas/historia/${historiaEspecialidadId}`);
      return {
        data: response.data?.estadisticas || {}
      };
    } catch (error) {
      // Mock data para desarrollo
      if (error.response?.status === 404 || !navigator.onLine) {
        return {
          data: {
            total_documentos: 5,
            total_size: 1024000,
            por_tipo: {
              'PDF': 3,
              'DOC': 1,
              'IMG': 1
            },
            ultimos_30_dias: 3
          }
        };
      }
      throw error;
    }
  }

  async getDocumentosByHistoria(historiaId) {
  try {
    const response = await this.axiosInstance.get(`/historia/${historiaId}`);
    return {
      data: response.data?.data || response.data || []
    };
  } catch (error) {
    console.error('Error al obtener documentos por historia:', error);
    // Devolver array vacío en caso de error
    return { data: [] };
  }
}

async getCategorias() {
  try {
    const response = await this.axiosInstance.get('/tipos');
    return {
      data: response.data?.data || response.data?.tipos || []
    };
  } catch (error) {
    console.error('Error al obtener categorías:', error);
    return { data: [] };
  }
}

async download(documentoId) {
  try {
    const response = await this.axiosInstance.get(`/${documentoId}/descargar`, {
      responseType: 'blob'
    });

    // Crear URL temporal para descarga
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    
    // Nombre del archivo
    const filename = `documento_${documentoId}.pdf`;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

  } catch (error) {
    console.error('Error al descargar documento:', error);
    throw new Error('Error al descargar documento');
  }
}

async delete(documentoId) {
  try {
    const response = await this.axiosInstance.delete(`/${documentoId}`);
    return {
      success: true,
      message: response.data?.message || 'Documento eliminado correctamente'
    };
  } catch (error) {
    console.error('Error al eliminar documento:', error);
    throw new Error('Error al eliminar documento');
  }
}

// Previsualizar documento (NUEVO MÉTODO)
async previewDocument(documentoId) {
  try {
    const response = await this.axiosInstance.get(`/${documentoId}/preview`, {
      responseType: 'blob'
    });
    
    // Crear URL temporal para previsualización
    const blob = response.data;
    const url = URL.createObjectURL(blob);
    
    return {
      success: true,
      url: url,
      blob: blob
    };
  } catch (error) {
    console.error('Error al previsualizar documento:', error);
    throw new Error('Error al previsualizar documento');
  }
}

}

// Exportar instancia singleton
const documentoService = new DocumentoService();
export default documentoService;